import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import StarRating from "react-star-ratings";
import { getProductPrimaryImage } from "../auth/elakay-utils";
import { toImageUrl } from "../../config";
import { elakayAutoText } from "./dataElakay";
import imgProductLakay from "../Layout/imgs/md3.jpg";
import "./ProductCompareTable.css";

const productThumb = (product) =>
    toImageUrl(getProductPrimaryImage(product)) || imgProductLakay;

const showPlaceholderImage = (event) => {
    const img = event.currentTarget;
    if (img.dataset.fallback) return;
    img.dataset.fallback = "true";
    img.src = imgProductLakay;
};

// Shown for a value the API did not provide: a missing price is never "$0.00"
// and an unknown stock is never "Out of stock".
const UNKNOWN_VALUE = "-";

// A column is drawn only for a resolved product. A saved reference the API
// returned as a bare id (or null) is not a product: it used to render as an
// empty column -- no name, no image, "$0.00", "Out of stock".
export const resolvedCompareProducts = (entries) =>
    (Array.isArray(entries) ? entries : []).filter(
        (entry) => entry && typeof entry === "object" && entry._id && entry.name
    );

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const getShopName = (product) => {
    const address = product?.owner?.address?.find((a) => String(a?._id) === String(product?.addressShop))
        || product?.owner?.address?.find((a) => a?.addressDefault)
        || product?.owner?.address?.[0];
    return address?.shopName || [address?.firstName, address?.lastName].filter(Boolean).join(" ") || "-";
};

const getFinalPrice = (product) => {
    const price = Number(product?.price);
    // A public product always has a price above 0: anything else is unknown.
    if (!Number.isFinite(price) || price <= 0) return UNKNOWN_VALUE;
    const sale = Number(product?.salePrice || 0);
    const finalPrice = sale > 0 ? (price * (100 - sale)) / 100 : price;
    return `$${finalPrice.toFixed(2)}`;
};

// Stock of a product = sum of its variant quantities, the source the product
// card and the product page already use (a Dropshipping Out product without
// variant rows falls back to its supplier stock). `null` when the payload
// carries no quantity at all. sellActive.sell is the "published" switch, not
// a stock level.
export const getCompareStock = (product) => {
    const quantities = (Array.isArray(product?.filterProduct) ? product.filterProduct : [])
        .map((variant) => Number(variant?.quantity))
        .filter(Number.isFinite);
    if (quantities.length) return quantities.reduce((sum, quantity) => sum + quantity, 0);
    const supplierStock = product?.dropshippingPublic?.stockQuantity;
    return Number.isFinite(supplierStock) ? supplierStock : null;
};

const getFilterKeys = (products) => {
    const keys = new Set();
    products.forEach((product) => {
        const filter = product?.filterProduct?.[0]?.filter;
        if (filter && typeof filter === "object") {
            Object.keys(filter).forEach((key) => keys.add(key));
        }
    });
    return [...keys];
};

const DESCRIPTION_PREVIEW_LENGTH = 90;

const ProductCompareTable = ({ currentProduct, currentSeller = null, compareProducts = [] }) => {
    const [showOnlyDiff, setShowOnlyDiff] = useState(false);
    const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());

    const toggleDescription = (productId) => {
        setExpandedDescriptions((prev) => {
            const next = new Set(prev);
            if (next.has(productId)) next.delete(productId);
            else next.add(productId);
            return next;
        });
    };

    const products = useMemo(() => {
        // The page of a Dropshipping Out product carries its owner as an id
        // only ("Sold by" showed "-"); the API returns the seller separately.
        const hasOwner = currentProduct?.owner && typeof currentProduct.owner === "object";
        const current = currentProduct && currentSeller && !hasOwner
            ? { ...currentProduct, owner: currentSeller }
            : currentProduct;
        return [current, ...resolvedCompareProducts(compareProducts)].filter(Boolean);
    }, [currentProduct, currentSeller, compareProducts]);

    const rows = useMemo(() => {
        const filterKeys = getFilterKeys(products);
        const fixedRows = [
            { key: "price", label: elakayAutoText("h_3e8248e32edfca", "Price"), value: getFinalPrice },
            {
                key: "description",
                label: elakayAutoText("h_55f8ebc805e65b", "Description"),
                value: (p) => p?.description || "-",
                cell: (p) => {
                    const text = p?.description || "-";
                    const isLong = text.length > DESCRIPTION_PREVIEW_LENGTH;
                    const isExpanded = expandedDescriptions.has(p._id);
                    const shown = !isLong || isExpanded ? text : `${text.slice(0, DESCRIPTION_PREVIEW_LENGTH).trim()}...`;

                    return (
                        <span className="compare-description">
                            {shown}
                            {isLong && (
                                <button
                                    type="button"
                                    className="compare-description-toggle"
                                    onClick={() => toggleDescription(p._id)}
                                >
                                    {isExpanded
                                        ? elakayAutoText("h_47c77bf0a809f2", "See less")
                                        : elakayAutoText("h_c510d6c04c0951", "See more")}
                                </button>
                            )}
                        </span>
                    );
                },
            },
            { key: "vendor", label: elakayAutoText("h_12c71f0ae42b56", "Sold by"), value: getShopName },
            {
                key: "availability",
                label: elakayAutoText("h_681b5b5ae10b99", "Availability"),
                value: (p) => {
                    const stock = getCompareStock(p);
                    if (stock === null) return UNKNOWN_VALUE;
                    return stock > 0
                        ? elakayAutoText("h_3f1356ad0e5ce4", "In stock")
                        : elakayAutoText("h_8b78c7aec41533", "Out of stock");
                },
                className: (p) => {
                    const stock = getCompareStock(p);
                    if (stock === null) return "";
                    return stock > 0 ? "compare-instock" : "compare-outofstock";
                },
            },
            {
                key: "rating",
                label: elakayAutoText("h_6437b7bf655854", "Rating"),
                value: (p) => `${Number(p?.Stars || 0).toFixed(1)}|${p?.comments?.length || 0}`,
                cell: (p) => (
                    <div className="compare-rating">
                        <StarRating
                            rating={Number(p?.Stars || 0)}
                            numberOfStars={5}
                            starRatedColor="goldenRod"
                            starDimension="16px"
                            starSpacing="1px"
                        />
                        <span className="compare-rating-count">
                            {p?.comments?.length || 0} {elakayAutoText("h_7b2c6e04fe9dbd", "reviews")}
                        </span>
                    </div>
                ),
            },
        ];

        const dynamicRows = filterKeys.map((filterKey) => ({
            key: `filter-${filterKey}`,
            label: capitalize(filterKey),
            value: (p) => {
                const v = p?.filterProduct?.[0]?.filter?.[filterKey];
                return v === undefined || v === null || v === "" ? "-" : String(v);
            },
        }));

        const allRows = [...fixedRows, ...dynamicRows];

        if (!showOnlyDiff) return allRows;

        return allRows.filter((row) => {
            const values = products.map((p) => row.value(p));
            return new Set(values).size > 1;
        });
    }, [products, showOnlyDiff, expandedDescriptions]);

    if (products.length < 2) return null;

    return (
        <div className="prod-compare-table-wrap">
            <div className="prod-compare-table-header">
                <h3 className="prod-compare-table-title">
                    {elakayAutoText("h_e4390e2cc97a4e", "Product Comparison")}
                </h3>
                <label className="prod-compare-table-diff-toggle">
                    <input
                        type="checkbox"
                        checked={showOnlyDiff}
                        onChange={(e) => setShowOnlyDiff(e.target.checked)}
                    />
                    {elakayAutoText("h_7e0d7ed50d552f", "Show only differences")}
                </label>
            </div>

            <div className="prod-compare-table-scroll">
                <table className="prod-compare-table">
                    <thead>
                        <tr>
                            <th className="prod-compare-table-rowlabel" />
                            {products.map((product, index) => (
                                <th key={product._id} className={index === 0 ? "compare-current-col" : ""}>
                                    <img src={productThumb(product)} alt={product.name} onError={showPlaceholderImage} />
                                    {index === 0 ? (
                                        <>
                                            <span className="compare-product-name">{product.name}</span>
                                            <span className="compare-current-badge">
                                                {elakayAutoText("h_9826660fbf964c", "This product")}
                                            </span>
                                        </>
                                    ) : (
                                        <Link to={`/product/preview/${product.publicId || product._id}`} className="compare-product-name">
                                            {product.name}
                                        </Link>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.key}>
                                <th className="prod-compare-table-rowlabel">{row.label}</th>
                                {products.map((product, index) => (
                                    <td
                                        key={product._id}
                                        className={[
                                            index === 0 ? "compare-current-col" : "",
                                            row.className ? row.className(product) : "",
                                        ].filter(Boolean).join(" ")}
                                    >
                                        {row.cell ? row.cell(product) : row.value(product)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ProductCompareTable;
